import React, { useState } from 'react';

import { PageHeader, notification } from 'antd';

import styled from 'styled-components';

import { removeStatementLog } from '@libs/NwLogs';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useDeleteCreditNote } from '@hooks/API/UseCreditNotes';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import StyleVariables from '@components/Gui/StyleVariables';
import ChangeAccountDrawer from '@components/InvoicesAndCreditNotes/Common/ChangeAccountCodeDrawer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const CreditNoteHeader = styled.div`
    z-index: 1030;
    top: ${(props) => (props.$inDrawer ? '0' : '5rem')};
    width: 100%;
    position: ${(props) => (props.$inDrawer ? 'relative' : 'sticky')};

    .ant-page-header {
        margin: 0 0.5rem 0.5rem 0.5rem;
        padding: 0;
        border-bottom: ${(props) => props.theme.DefaultBorder};
        height: 5rem;

        .ant-page-header-heading-left {
            margin: 0;
        }

        .invoice-header-wrapper {
            display: flex;
            justify-content: flex-start;
            align-items: center;

            .invoice-header-wrapper-left,
            .invoice-header-wrapper-right {
                height: 5rem;
                padding-top: 1rem;

                small {
                    display: block;
                    font-size: ${(props) => props.theme.FontSizeBase};
                    text-transform: uppercase;
                    font-weight: 600;
                    margin: 0 0 0.25rem 0;
                    line-height: 1.4em;
                }
                h2 {
                    font-size: ${(props) => props.theme.FontSizeSm};
                    margin: 0;
                    line-height: 1em;
                    font-weight: 300;
                }
            }

            .invoice-header-wrapper-left {
                border-right: 10px solid ${(props) => props.theme.LayoutBodyBackground};
                width: 460px;
                padding-left: 1rem;
            }

            .invoice-header-wrapper-right {
                flex-grow: 1;
                padding-left: 2rem;
                padding-right: 6rem;
            }
        }

        .ant-page-header-heading-extra {
            padding: 10px 20px 0 0;
        }
    }

    .confirm-button {
        color: ${StyleVariables.PrimaryColor};
        text-decoration: underline;
        cursor: pointer;
        user-select: none;
        font-size: 14px;
        font-weight: 500;
    }
`;

const CreditNotePageHeader = ({
    creditNoteData,
    handleShowEditDrawer,
    inDrawer,
    locked,
    onDelete,
    originalInvoiceData,
}) => {
    const { mutateAsync: onDeleteCreditNote } = useDeleteCreditNote();

    const [showChangeAccountCodeDrawer, setShowChangeAccountCodeDrawer] = useState(false);

    const configurations = useConfigurations();
    const accounts = [];
    if (configurations && configurations.COA) {
        const COAjs = JSON.parse(configurations.COA);
        if (COAjs.accounts && COAjs.accounts.length > 0) {
            accounts.push(...COAjs.accounts);
        }
    }
    const enableCOA = accounts.length > 0;

    const usedIDs = [];
    const tempCreditNoteData = creditNoteData.Rows.map((item, index) => {
        const statement = item.Statement;
        if (!statement)
            return {
                key: index,
                index: item.ID,
                Guid: item.GroupGuid,
                RowIds: [{ ID: item.ID, Type: 'Text' }],
                Type: 'Text',
            };
        return {
            key: index,
            index: item.ID,
            Guid: item.GroupGuid,
            RowIds: [{ ID: item.ID, Type: 'Statement' }],
            Type: 'Statement',
        };
    });
    const groupedCreditNoteData = tempCreditNoteData
        .map((row) => {
            if (row.Guid === null) return row;
            if (usedIDs.filter((guid) => guid === row.Guid).length === 0) {
                usedIDs.push(row.Guid);
                const group = tempCreditNoteData.filter((el) => el.Guid === row.Guid);
                return {
                    ...row,
                    RowIds: group.map((row) => ({
                        ID: row.index,
                        Type: row.Type,
                    })),
                };
            }
            return null;
        })
        .filter((el) => el !== null);

    const onDeleteWholeCreditNote = async () => {
        try {
            await onDeleteCreditNote(creditNoteData.ID);
            removeStatementLog(creditNoteData.ID);
            onDelete();
        } catch (e) {
            notification.error({
                message: 'Error deleting credit note',
                description: e?.message || 'Unknown error',
            });
        }
    };

    const getCreditNoteLabel = () => {
        if (creditNoteData.TypeName === 'CreditModel') {
            return 'Model Expenses Credit Note';
        }
        if (creditNoteData.TypeName === 'CreditAgency') {
            return 'Agency Commissions Credit Note';
        }
        return 'Client Credit Note';
    };

    const dropdownActions = [
        {
            name: '',
            key: 'edit',
            disabled: locked,
            pinned: true,
            icon: byPrefixAndName.fasl['pen'],
            action: () => handleShowEditDrawer(),
        },
        {
            name: 'Delete',
            key: 'delete',
            disabled: locked,
            action: () => onDeleteWholeCreditNote(),
        },
    ];

    if (enableCOA) {
        dropdownActions.push({
            name: 'Set Account Code on all lines',
            key: 'coa',
            action: () => setShowChangeAccountCodeDrawer(true),
        });
    }

    return (
        <>
            <CreditNoteHeader $inDrawer={inDrawer}>
                <PageHeader
                    title={
                        <div className="invoice-header-wrapper">
                            <div className="invoice-header-wrapper-left">
                                <small>ORIGINAL INVOICE</small>
                                <h2>{originalInvoiceData ? originalInvoiceData.Name : ''}</h2>
                            </div>
                            <div className="invoice-header-wrapper-right">
                                <small>{getCreditNoteLabel()}</small>
                                <h2>
                                    {getContactName(creditNoteData.ContactID)} <em>-</em> {creditNoteData.Name}
                                </h2>
                            </div>
                        </div>
                    }
                    subTitle={false}
                    extra={[<NwDropDownMenu size="small" key="extra" actions={dropdownActions} />]}
                ></PageHeader>
            </CreditNoteHeader>
            {showChangeAccountCodeDrawer && (
                <ChangeAccountDrawer
                    creditNoteID={creditNoteData.ID}
                    records={[...groupedCreditNoteData]}
                    onClose={() => setShowChangeAccountCodeDrawer(false)}
                />
            )}
        </>
    );
};

export default CreditNotePageHeader;
